import React from "react";
import WorkCodeField from "../../../../../shared/WorkCodeField/WorkCodeField";
import { swatchHex } from "../../../../../../utils/color";
import type { ExistingWorkCode } from "../../../../../../models";
import "./BulkAssign.css";

interface Props {
  scheduleId: number;
  /** How many activities are selected. */
  count: number;
  onAssign: (code: string, name: string, color: string) => void;
  /** Codes created earlier this session (so they're reusable here). */
  extraCodes?: ExistingWorkCode[];
}

/** The "Set work code" trigger for the selection bar: a button that opens the
 *  shared picker as a popover ABOVE the bar (on demand — never auto), with an
 *  "Assign to N" footer that applies the picked/created code to every selection. */
const BulkAssign: React.FC<Props> = ({ scheduleId, count, onAssign, extraCodes }) => {
  const [open, setOpen] = React.useState(false);
  const [buf, setBuf] = React.useState({ code: "", name: "", color: "" });
  const wrapRef = React.useRef<HTMLDivElement>(null);

  // Selection cleared (or assigned) → close + drop the buffer.
  React.useEffect(() => {
    if (count === 0) {
      setOpen(false);
      setBuf({ code: "", name: "", color: "" });
    }
  }, [count]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const reset = () => {
    setBuf({ code: "", name: "", color: "" });
    setOpen(false);
  };
  const assign = () => {
    const code = buf.code.trim();
    if (!code) return;
    // Save what the swatch shows: the picked color, or the derived hue when the
    // user didn't open the color picker (otherwise the code saves with no color).
    onAssign(code, buf.name, swatchHex(buf.color, code));
    reset();
  };

  return (
    <div className="takt-editor-bulk" ref={wrapRef}>
      <button
        type="button"
        className="takt-editor-bulk-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={count === 0}
        onClick={() => setOpen((o) => !o)}
        title={
          count === 0
            ? "Select activities to assign a work code"
            : "Assign a work code to the selected activities"
        }
      >
        ＋ Set work code
      </button>
      {open && count > 0 && (
        <div className="takt-editor-bulk-pop">
          <WorkCodeField
            inline
            scheduleId={scheduleId}
            code={buf.code}
            name={buf.name}
            color={buf.color}
            onChange={(p) => setBuf((b) => ({ ...b, ...p }))}
            extraCodes={extraCodes}
            footer={
              <button
                type="button"
                className="takt-editor-bulk-apply"
                disabled={!buf.code.trim()}
                onClick={assign}
              >
                Assign{buf.code.trim() ? ` ${buf.code.trim()}` : ""} to {count}
              </button>
            }
          />
        </div>
      )}
    </div>
  );
};

export default BulkAssign;
